import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Card, Spin, Typography, Tag, Alert, Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  getReportedById,
  getPmpCrfRecord,
} from "../../../../../../services/data/patientSelfReported";
import "./pmpScaleForm.less";

const { Title, Text, Paragraph } = Typography;

interface PmpScaleFormProps {
  scaleCode?: string;
  visitId?: string | number;
  subjectCode?: string;
  recordId?: string | number;
}

interface FieldNode {
  type?: string;
  label?: string;
  fieldId?: string;
  nodeType?: string;
  required?: boolean;
  options?: Array<{ label: string; value: any; score?: number }>;
  children?: FieldNode[];
}

interface SchemaSnapshot {
  name?: string;
  description?: string;
  root?: FieldNode;
}

const PmpScaleForm: React.FC<PmpScaleFormProps> = (props) => {
  const history = useHistory();
  const location = useLocation();
  // 优先取路由跳转带过来的 state，其次取组件 props
  const state: any = location.state || {};
  const scaleCode = props.scaleCode ?? state.scaleCode;
  const visitId = props.visitId ?? state.visitId;
  const recordIdProp = props.recordId ?? state.recordId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [record, setRecord] = useState<any>(null);
  const [schema, setSchema] = useState<SchemaSnapshot | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [submitTime, setSubmitTime] = useState<string>("");

  // 解析 schema + 数据
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. 解析出 PMP 用的 recordId（scalaServerRecordId）
        let pmpRecordId = recordIdProp;
        if (!pmpRecordId && scaleCode && visitId) {
          const { data } = await getReportedById({ scaleCode, visitId });
          const list = data ?? [];
          pmpRecordId = list.at(-1)?.scalaServerRecordId;
        }

        if (!pmpRecordId) {
          throw new Error("未找到对应的量表记录 ID");
        }

        // 2. 调用自有后端 /report/crf/get（后端转发到 PMP）
        const res = await getPmpCrfRecord(pmpRecordId);
        if (res.code !== 0) {
          throw new Error(res.msg || "PMP 接口返回异常");
        }
        const data = res.data;
        if (!mounted) return;

        setRecord(data);
        if (data.schemaSnapshot) {
          setSchema(
            typeof data.schemaSnapshot === "string"
              ? JSON.parse(data.schemaSnapshot)
              : data.schemaSnapshot
          );
        }
        if (data.dataJson) {
          setValues(
            typeof data.dataJson === "string"
              ? JSON.parse(data.dataJson)
              : data.dataJson
          );
        }
        if (data.submitTime) {
          setSubmitTime(
            moment(Number(data.submitTime)).format("YYYY-MM-DD HH:mm:ss")
          );
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || "加载失败");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [scaleCode, visitId, recordIdProp]);

  // 把 node-tree 拍平成字段列表
  const fieldList = useMemo(() => {
    if (!schema?.root) return [];
    const result: Array<{ section: string; field: FieldNode }> = [];
    const walk = (node: FieldNode, sectionTitle: string) => {
      if (node.nodeType === "field" || node.type) {
        result.push({ section: sectionTitle, field: node });
      }
      if (node.children) {
        node.children.forEach((child) =>
          walk(child, node.type ? sectionTitle : (node as any).title || sectionTitle)
        );
      }
    };
    walk(schema.root, "");
    return result;
  }, [schema]);

  const renderFieldValue = (field: FieldNode) => {
    const raw = values?.[field.fieldId as string];
    if (raw === undefined || raw === null || raw === "") {
      return <Text type="secondary">—</Text>;
    }
    if (field.type === "likert" && Array.isArray(field.options)) {
      const opt = field.options.find((o) => String(o.value) === String(raw));
      return (
        <Tag color="blue">{opt ? opt.label : raw}</Tag>
      );
    }
    return <Text>{String(raw)}</Text>;
  };

  return (
    <div className="pmp-scale-form">
      <div className="pmp-scale-form__header">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => history.goBack()}
        >
          返回
        </Button>
        <Title level={4} style={{ margin: "8px 0" }}>
          {record?.crfName || schema?.name || "量表记录"}
        </Title>
        {submitTime && (
          <Text type="secondary">提交时间：{submitTime}</Text>
        )}
      </div>

      {loading && (
        <div className="pmp-scale-form__loading">
          <Spin tip="加载中..." />
        </div>
      )}

      {!loading && error && (
        <Alert type="error" showIcon message="加载失败" description={error} />
      )}

      {!loading && !error && schema?.description && (
        <Paragraph className="pmp-scale-form__desc">
          {schema.description}
        </Paragraph>
      )}

      {!loading && !error && (
        <div className="pmp-scale-form__body">
          {fieldList.length === 0 && (
            <Alert type="info" showIcon message="暂无量表字段" />
          )}
          {fieldList.map(({ section, field }, idx) => (
            <Card
              key={field.fieldId || idx}
              className="pmp-scale-form__card"
              size="small"
            >
              {section && (
                <div className="pmp-scale-form__section">{section}</div>
              )}
              <div className="pmp-scale-form__field">
                <Text strong>{field.label}</Text>
                <div className="pmp-scale-form__value">
                  {renderFieldValue(field)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PmpScaleForm;
